/**
 * Handles dynamic module import failures with automatic retry mechanism.
 *
 * This module intercepts errors from lazy-loaded modules (including both our React.lazy()
 * components and Ionic's internal modules like input-shims) and attempts one automatic
 * retry by reloading the page.
 *
 * Common causes of chunk load failures:
 * - Network interruptions on mobile devices
 * - Slow/unstable connections timing out during module fetch
 * - CORS issues in iframe contexts
 *
 * The retry mechanism:
 * 1. First error → Reload page with ?__retry=1 parameter
 * 2. Success → Clean up the retry parameter from URL
 * 3. Second error → Let it bubble to Sentry for investigation
 *
 * This prevents most transient network errors from reaching Sentry while still
 * capturing persistent issues that need attention.
 *
 * @see https://clarity-movement.sentry.io/issues/OPENMAP-D
 */

import * as Sentry from '@sentry/react';

const RETRY_PARAM = '__retry';

/**
 * Safely logs a message to Sentry if it's initialized, otherwise logs to console
 */
function safeLogToSentry(message: string, level: 'info' | 'warning' = 'info', extra?: Record<string, unknown>) {
  try {
    Sentry.captureMessage(message, {
      level,
      tags: {
        error_type: 'chunk_load_failure',
      },
      extra,
    });
  } catch {
    // Fallback to console logging if Sentry isn't initialized yet
    console.log(`[Sentry not initialized] ${message}`, extra);
  }
}

/**
 * Checks if an error is a chunk load failure and handles retry logic.
 * Returns true if the error was handled (retry initiated), false otherwise.
 */
function handleChunkLoadError(errorMessage: string): boolean {
  // Check if this is a dynamic import failure
  if (
    errorMessage.includes('Failed to fetch dynamically imported module') ||
    errorMessage.includes('error loading dynamically imported module') ||
    errorMessage.includes('Importing a module script failed') ||
    errorMessage.includes('Failed to fetch') // Generic fetch failure
  ) {
    const url = new URL(window.location.href);
    const hasRetried = url.searchParams.has(RETRY_PARAM);

    if (!hasRetried) {
      console.warn('[Chunk Load Error] Attempting recovery by reloading page...', errorMessage);

      // Log the reload attempt to Sentry for monitoring
      safeLogToSentry('Chunk load error recovery: page reload initiated', 'info', {
        original_error: errorMessage,
        recovery_action: 'page_reload',
      });

      // Add retry parameter to URL
      url.searchParams.set(RETRY_PARAM, '1');

      // Reload with retry parameter after brief delay to allow network recovery
      setTimeout(() => {
        window.location.href = url.toString();
      }, 500);

      return true; // Handled
    }

    // Already retried - let error bubble to Sentry for investigation
    console.error('[Chunk Load Error] Retry failed, reporting to Sentry', errorMessage);

    // Log the failed retry attempt
    safeLogToSentry('Chunk load error recovery failed after retry', 'warning', {
      original_error: errorMessage,
      recovery_action: 'retry_failed',
    });
  }

  return false; // Not handled
}

/**
 * Installs global error handlers that intercept chunk load failures.
 */
export function installChunkLoadErrorHandler(): void {
  // Handle synchronous errors (e.g., script load failures)
  window.addEventListener(
    'error',
    (event) => {
      const errorMessage = event.message || event.error?.message || '';

      if (handleChunkLoadError(errorMessage)) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    true
  ); // Use capture phase to intercept before other handlers (including Sentry)

  // Handle unhandled promise rejections (e.g., dynamic import() failures)
  window.addEventListener('unhandledrejection', (event) => {
    const errorMessage = event.reason?.message || event.reason?.toString() || '';

    if (handleChunkLoadError(errorMessage)) {
      event.preventDefault();
      // Note: stopPropagation() doesn't exist on PromiseRejectionEvent
    }
  });

  // Clean up retry parameter after successful load
  window.addEventListener('load', () => {
    const url = new URL(window.location.href);
    if (url.searchParams.has(RETRY_PARAM)) {
      url.searchParams.delete(RETRY_PARAM);
      // Use replaceState to avoid adding to browser history
      window.history.replaceState({}, '', url.toString());
    }
  });
}
